import { all, takeEvery, select, put, call } from 'redux-saga/effects';
import {
  PapiDaSharedCorebadgeGivers,
  PapiRequestDaSharedCorebadgeGivers,
  PapiRequestDaSharedCorebadgeAward,
  PapiDaSharedCorebadgeAvailableBadges,
  PapiDeviation,
  PapiComment,
  PapiRequestDaSharedCorebadgeAvailableBadges,
} from '@wix/da-papi-types';
import i18next from 'i18next';
import { requestPuppy } from '@wix/da-http-client';
import {
  awardBadge,
  awardBadgeFail,
  awardBadgeSuccess,
  fetchAvailableBadges,
  fetchAvailableBadgesSuccess,
  fetchBadgeGiverStreamNext,
  awardingBadge,
} from './actionCreators';
import { getIsMobile } from '../../publicSession/selectors';
import { removeModalsOfType } from '../../Modals/redux/basicActions';
import { ModalType } from '../../Modals/redux/types';
import { addNotificationToQueue } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications/redux/types';
import { setWatchingState } from '../../WatchButton/redux/actionCreators';
import { getDeviationNotificationQueue } from '../../DeviationPage/redux/helpers';
import { getCommentNotificationQueue } from '../../Comment/utils';
import { withOffset, selectors as streamSelectors } from '../../Stream';
import {
  CoreBadgeRecipient,
  BadgeNotificationParams,
  AwardBadgeSource,
} from '../types';
import { DeviationResourceType } from '../../types/deviation';
import { ResourceType } from '../../types/resource';
import { getIsAwarding } from './selectors';

function getFakeStreamData() {
  if (process.env.NODE_ENV !== 'production') {
    const { users } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    const { Chance } = require('chance'); // eslint-disable-line global-require
    const chance = new Chance();
    return {
      hasMore: false,
      nextOffset: null,
      results: chance.pickset(users, 20).map(user => ({
        ...user,
        isWatching: chance.bool(),
        profile: { tagline: chance.bool() ? chance.sentence() : '' },
      })),
    };
  }
  return false;
}

function* handleAwardBadge(action: ReturnType<typeof awardBadge>) {
  const { badgeType, recipient, source } = action.payload;
  const isAwarding = yield select(getIsAwarding);
  if (isAwarding) {
    return;
  }

  const data: PapiRequestDaSharedCorebadgeAward = {
    badgetype: badgeType,
    typeid: recipient.typeId,
    itemid: recipient.itemId,
    userid: recipient.user.userId,
  };
  yield put(awardingBadge());
  const result = yield call(requestPuppy, {
    method: 'post',
    url: 'corebadge/award',
    data,
  });

  if (result && result.badge) {
    // Close the modal first
    yield put(removeModalsOfType(ModalType.AWARD_BADGE));
    const newBalance = result.balance;
    const allBadges = result.allBadges ?? [];
    yield put(
      awardBadgeSuccess({
        badge: result.badge,
        recipient,
        source,
        newBalance,
        allBadges,
      })
    );
  } else {
    yield put(
      awardBadgeFail(
        badgeType,
        recipient,
        source,
        result.errorDescription || i18next.t('common.somethingWentWrong')
      )
    );
  }
}

function* handleAwardBadgeSuccess(
  action: ReturnType<typeof awardBadgeSuccess>
) {
  const { badge, recipient, source } = action.payload;
  const notification: BadgeNotificationParams = {
    badge,
    recipient,
    source,
  };
  const isMobile = yield select(getIsMobile);
  yield put(
    addNotificationToQueue(
      source === AwardBadgeSource.THUMB_OVERLAY
        ? 'default'
        : getBadgeNotificationQueue(recipient, isMobile),
      NotificationType.Badge,
      notification
    )
  );
}

/** returns proper queuename based on what's being awarded a badge */
function getBadgeNotificationQueue(
  recipient: BadgeNotificationParams['recipient'],
  isMobile?: boolean
): string {
  if (isMobile) {
    return 'default';
  }

  if (DeviationResourceType[recipient.typeId]) {
    // It's not especially pretty, but the queue only needs type and id
    return getDeviationNotificationQueue({
      typeId: recipient.typeId,
      deviationId: recipient.itemId,
    } as PapiDeviation);
  }

  if (recipient.typeId === ResourceType.COMMENT) {
    return getCommentNotificationQueue({
      commentId: recipient.itemId,
    } as PapiComment);
  }

  return 'default';
}

function* handleFetchAvailableBadges(
  action: ReturnType<typeof fetchAvailableBadges>
) {
  const { userId, typeId, itemId } = action.payload;
  const params: PapiRequestDaSharedCorebadgeAvailableBadges = {
    userid: userId,
    typeid: typeId,
    itemid: itemId,
  };
  const result: PapiDaSharedCorebadgeAvailableBadges = yield call(
    requestPuppy,
    { method: 'get', url: 'corebadge/available_badges', params }
  );
  if (result) {
    yield put(
      fetchAvailableBadgesSuccess(
        userId,
        result.results,
        result.gemRestrictions
      )
    );
  }
}

function* handleStreamFetch(
  action: ReturnType<typeof fetchBadgeGiverStreamNext>
) {
  const { streamId } = action.payload;
  const streamParams = yield select(streamSelectors.getStreamParams, streamId);
  const { offset, limit } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: streamSelectors.getItemsPerFetch(state, streamId),
  }));
  const recipient: CoreBadgeRecipient = streamParams.recipient;
  const params: PapiRequestDaSharedCorebadgeGivers = {
    badgetype: streamParams.type,
    typeid: recipient.typeId,
    itemid: recipient.itemId,
    userid: recipient.user.userId,
    offset,
    limit,
  };
  const result: PapiDaSharedCorebadgeGivers = yield call(
    requestPuppy,
    { method: 'get', url: 'corebadge/givers', params },
    getFakeStreamData
  );
  if (result) {
    const { hasMore, nextOffset, results } = result;
    yield all([
      ...results.map(user =>
        put(setWatchingState(user.username, user.isWatching ?? false))
      ),
      put(
        withOffset.actions.fetchSuccess({
          streamId,
          hasMore: !!hasMore,
          nextOffset: nextOffset ?? undefined,
          items: results,
        })
      ),
    ]);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(awardBadge, handleAwardBadge),
    takeEvery(awardBadgeSuccess, handleAwardBadgeSuccess),
    takeEvery(fetchBadgeGiverStreamNext, handleStreamFetch),
    takeEvery(fetchAvailableBadges, handleFetchAvailableBadges),
  ]);
}
